import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, takeWhile } from 'rxjs/operators';
import { CoreCompanyApiService } from './core-company-api.service';
import { Company, Site } from '../models/core-company-api.model';
import { UserProfile } from '../models/core-user-api.model';
import { AuthService } from '@auth0/auth0-angular';
import { VdoUserAuthService } from './vdo-user.service';
import { mapUser } from '../models/user.mapper';
import { VdoUser } from '../models/vdo-user';
import { environment } from '@environments/environment';
import { SettingsFacade } from '@app/modules/settings/facade/settings.facade';
import { VdoApiUserV2 } from '../models/vdo-user.model';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  permissions$: ReplaySubject<object> = new ReplaySubject(1);
  private fetchPermsSuccess$: ReplaySubject<boolean> = new ReplaySubject(1);
  permissionFormatError = 'Invalid permission format. Permission string must contain two or six fields.';
  entityTypes = ['divisions', 'companies', 'applications'];
  invalidEntityTypeError = `entityType must be one of ${this.entityTypes}`;
  unrestrictedApps = '_unrestrictedApps';
  isZonarUser$: ReplaySubject<boolean> = new ReplaySubject(1);
  userProfiles$: ReplaySubject<UserProfile[]> = new ReplaySubject(1);
  users$: ReplaySubject<VdoUser[]> = new ReplaySubject<VdoUser[]>(1);
  zonarUserProfile$: ReplaySubject<UserProfile> = new ReplaySubject(1);
  companyMap$: ReplaySubject<any> = new ReplaySubject(1);
  divisionMap$: ReplaySubject<any> = new ReplaySubject(1);
  // divisionChildren$: ReplaySubject<any> = new ReplaySubject(1);
  companyDivisions$: ReplaySubject<any> = new ReplaySubject(1);
  private currentCompanyContext$: BehaviorSubject<Company> = new BehaviorSubject({} as Company);
  private userProfileExist = false;
  requestStartTime$: ReplaySubject<number> = new ReplaySubject(1);

  constructor(
    private coreCompanyApiService: CoreCompanyApiService,
    private authService: AuthService,
    private vdoUserAuthService: VdoUserAuthService,
    private settingsFacade: SettingsFacade,
    @Inject('applicationId') private readonly applicationId: string,
    @Inject('defaultPermPrefix') public readonly defaultPermPrefix: string
  ) {
    this.getPermissions();
  }

  _isZonarUserProfile(userProfile) {
    // the V1 entity APIs use a null companyId and empty division list to specify global company access for zonar users
    return userProfile.companyId === null && userProfile.divisions.length === 0;
    // return true
  }

  _checkZonarUser(userProfiles) {
    return userProfiles.some(profile => this._isZonarUserProfile(profile));
  }

  isfetchPermsSuccess(): Observable<boolean> {
    return this.fetchPermsSuccess$;
  }

  isZonarUser() {
    return this.isZonarUser$;
  }

  getCompanyMap() {
    return this.companyMap$;
  }

  getDivisionsMap() {
    return this.divisionMap$;
  }

  getRequestStartTime() {
    return this.requestStartTime$;
  }

  getZonarUserProfile(): Observable<UserProfile> {
    return this.zonarUserProfile$;
  }

  getUserProfiles() {
    return this.userProfiles$;
  }

  getUser() {
    // this.users$ is an array as it calls the /users endpoint but will only contain one element
    // which is the auth0 authenticated user.
    return this.users$.pipe(map(users => users[0]));
  }

  _getCompanyDivisions(companyId): Observable<Array<Site>> {
    return this.coreCompanyApiService.getSitesV2();
  }

  _buildPermissions(vdoUser: VdoUser, userProfile: UserProfile, divisions: Site[]) {
    const perms = {};

    (perms[this.unrestrictedApps] = perms[this.unrestrictedApps] || new Set()).add(environment.auth.applicationId);

    //const permissions = userPorfile.roles[0].permissions

    // TODO use permissions from vdoUser
    for (const role of userProfile.roles) {
      //     for (const perm of role.permissions) {
      for (const perm of role.permissions) {
        const [app, action, resource] = perm.split(':');
        perms[app] = perms[app] || {};
        perms[app][resource] = perms[app][resource] || {};
        perms[app][resource][action] = perms[app][resource][action] || {};

        const act = perms[app][resource][action];

        // (act.companies = act.companies || new Set<string>()).add(profile.companyId);
        //(act.applications = act.applications || new Set<string>()).add(userPorfile.applicationId);
        act.applications = new Set<string>();
        act.applications.add(userProfile.applicationId);
        act.companies = new Set<string>();
        if (userProfile.companyId) {
          act.companies.add(userProfile.companyId);
        }

        if (userProfile.divisions && userProfile.divisions.length) {
          act.divisions = new Set<string>(userProfile.divisions);
          //userPorfile.divisions.map(profileDivision => {
          // The profile division will have an entry with division children.
          //divisionChildren[profileDivision].map(division => act.divisions.add(division));
          //});
        } else {
          act.divisions = new Set<string>();
        }
      }
    }
    return perms;
  }

  // createEmptyVdoUser():VdoUser{
  //   return {
  //     id:0,
  //     sub:"",
  //     userName: "",
  //     userID: 0,
  //     permissions: [],
  //     account_id: 0,
  //     account_name: "",
  //     sp_id: "0",
  //     accountID: 0,
  //     accountName: "",
  //     spId: 0,
  //   }
  // }

  createEmptyVdoApiUser(): VdoApiUserV2 {
    return {
      vdoUserId: '',
      account: {
        id: '',
        displayName: '',
        organisationName: '',
        customerType: 0,
        solutionProviderId: 0,
        timezone: ''
      },
      grants: {
        userType: '',
        positionUndertaking: '',
        superUser: false,
        permissions: []
      },
      user: {
        preferences: {
          locale: ''
        },
        displayName: '',
        id: '',
        email: ''
      }
    };
  }

  getPermissions(allApps = false): void {
    this.requestStartTime$.next(Date.now());
    this.authService.user$
      .pipe(
        takeWhile(_ => !this.userProfileExist),
        switchMap(auth0User => {
          // the auth0User will be null when the user logs out. when this happens we need to clear all of the cached data
          if (!auth0User) {
            this.zonarUserProfile$.next(null);
            this.userProfiles$.next(null);
            this.companyMap$.next(null);
            this.divisionMap$.next(null);
            // this.divisionChildren$.next(null);
            this.companyDivisions$.next(null);
            this.permissions$.next(null);
          }
          const currentUser = auth0User.sub;
          const prevUser = localStorage.getItem('sub');
          if (currentUser !== prevUser) {
            this.settingsFacade.resetSettings();
            localStorage.setItem('sub', auth0User.sub);
          }
          // get vdo data on /me endpoint
          return this.vdoUserAuthService.loadUserProfile().pipe(
            map(vdoApiUser => {
              let vdoUser = mapUser(vdoApiUser, { ...auth0User });
              // permissions filtered in mapUser to only contain maps permissions
              // so if empty it means no map permissions
              if (!vdoUser.permissions?.length) {
                return of(mapUser(this.createEmptyVdoApiUser(), { ...auth0User }));
              }
              return vdoUser;
            }),
            // in case of 401
            catchError((e: HttpErrorResponse) => {
              if (e.status === 0) {
                // If request is cancelled or aborted
                return EMPTY;
              }
              return of(mapUser(this.createEmptyVdoApiUser(), { ...auth0User }));
            })
          );
        }),
        switchMap((vdoUser: VdoUser) => {
          // vdoUser.permissions = vdoUser.permissions.filter(item => item.startsWith("maps:"))
          this.users$.next([vdoUser]);

          // only one account / company for vdo user
          const company: Company = this.createDefaultCompany(vdoUser);
          // create a Zonar user profile to avoid major rework (company null and no divisions)
          let userProfile = this.createDefaultUserProfile(vdoUser);

          return forkJoin({
            vdoUser: of(vdoUser),
            userProfile: of(userProfile),
            company: of(company),
            sites: this._getCompanyDivisions(company.id)
          });
        }),
        switchMap((results: any) => {
          const userProfile = results.userProfile;
          const company = results.company;
          const divisionMap = {};
          results.sites.forEach((site: Site) => {
            if (site.isDefault) {
              company.defaultDivisionId = site.id;
            }
            divisionMap[site.id] = site;
            // TODO we might want to use filtering on site
            // userProfile.divisions.push(site.id)
          });

          this.setCurrentCompanyContext(company);

          const companyMap = {};
          companyMap[company.id] = company;
          this.companyMap$.next(companyMap);
          this.userProfiles$.next([userProfile]);
          this.isZonarUser$.next(true);
          this.zonarUserProfile$.next(userProfile);
          this.companyDivisions$.next(results.sites);
          this.divisionMap$.next(divisionMap);
          this.userProfileExist = true;

          return of({ vdoUser: results.vdoUser, userProfile: userProfile, divisions: results.sites });
        })
      )
      .subscribe(
        ({ vdoUser, userProfile, divisions }: { vdoUser: VdoUser; userProfile: UserProfile; divisions: Site[] }) => {
          const perms = this._buildPermissions(vdoUser, userProfile, divisions); //userProfiles, divisionChildren, companyDivisions);
          // In rare case where the perms is returned as empty object, emit an error.
          if (!perms || !Object.keys(perms).length) {
            this.fetchPermsSuccess$.next(false);
          } else {
            this.permissions$.next(perms);
          }
        },
        () => this.fetchPermsSuccess$.next(false)
      );
  }

  // for now create a Zonar user profile, no company and divisions = []
  // should have ['gtcx_maps:read:assets', 'gtcx_maps:read:companies', 'gtcx_maps:read:company'] permissions
  createDefaultUserProfile(vdoUser: VdoUser): UserProfile {
    return {
      id: '' + vdoUser.id,
      userId: '' + vdoUser.id,
      companyId: null, // vdoUser.account_id
      divisions: [],
      applicationId: environment.auth.applicationId,
      status: 'ACTIVE',
      employeeNumber: '',
      homeLocationId: '',
      companyEmail: '',
      roles: [
        {
          permissions: vdoUser.permissions.map(item => item.replace('maps:', this.defaultPermPrefix + ':'))
          // permissions: ['gtcx_maps:read:assets', 'gtcx_maps:read:companies', 'gtcx_maps:read:company']
        }
      ]
    };
  }

  // vdo rework, only one account per user
  createDefaultCompany(vdoUser: VdoUser): Company {
    return {
      id: '' + vdoUser.account_id,
      name: vdoUser.account_name,
      created: '',
      modified: '',
      anonymizedData: false,
      defaultDivisionId: ''
    };
  }

  _hasPermission(action, resource, appPrefix, divisionId, companyId, applicationId): Observable<boolean> {
    return this.permissions$.pipe(
      filter(perms => Boolean(perms)),
      filter(perms => Object.keys(perms).length > 0),
      concatMap(perms => {
        let hasPerm = false;
        let permAction;

        // If the perm is not present, go no farther.
        if (!perms[appPrefix] || !perms[appPrefix][resource] || !perms[appPrefix][resource][action]) {
          return of(false);
        }

        permAction = perms[appPrefix][resource][action];

        // If the user is a Zonar user, they have permission no matter the company or division,
        // otherwise they must have permission to the specified company and division.
        if (perms[this.unrestrictedApps] && perms[this.unrestrictedApps].has(applicationId)) {
          hasPerm = true;
        } else if (
          (!divisionId || permAction.divisions.has(divisionId)) &&
          (!companyId || permAction.companies.has(companyId)) &&
          permAction.applications.has(applicationId)
        ) {
          hasPerm = true;
        }

        return of(hasPerm);
      })
    );
  }

  parsePermission(permission): Array<string> {
    let action;
    let resource;
    let appPrefix = this.defaultPermPrefix;
    let divisionId;
    let companyId;
    let applicationId = this.applicationId;

    // Permissions are of the shape: <applicationId>:<companyId>:<divisionId>:<appPrefix>:<action>:<resource>
    // applicationId, companyId, divisionId, and appPrefix are all optional.
    // All colons are required if using one or more optional fields.
    const parts = permission.split(':');

    if (parts.length !== 2 && parts.length !== 6) {
      throw new Error(this.permissionFormatError);
    }

    if (parts.length === 2) {
      [action, resource] = parts;
    } else {
      [applicationId, companyId, divisionId, appPrefix, action, resource] = parts;
      applicationId = applicationId || this.applicationId;
      companyId = companyId || undefined;
      divisionId = divisionId || undefined;
      appPrefix = appPrefix || this.defaultPermPrefix;
    }

    return [action, resource, appPrefix, divisionId, companyId, applicationId];
  }

  hasPermission(permission): Observable<boolean> {
    const [action, resource, appPrefix, divisionId, companyId, applicationId] = this.parsePermission(permission);
    return this._hasPermission(action, resource, appPrefix, divisionId, companyId, applicationId);
  }

  doesCompanyContextExist$(): Observable<boolean> {
    return of(Boolean(Object.keys(this.currentCompanyContext$.getValue()).length));
  }

  getCurrentCompanyContext$(): Observable<Company> {
    return of(this.currentCompanyContext$.getValue());
  }

  setCurrentCompanyContext(company: Company): void {
    this.currentCompanyContext$.next(company);
  }

  // get current company with BehaviorSubject streaming.
  getCurrentCompany(): Observable<Company> {
    return this.currentCompanyContext$;
  }

  doesUserProfileExist(): boolean {
    return this.userProfileExist;
  }

  setCurrentCompanyContextById(companyId: string) {
    this.companyMap$.subscribe(companies => {
      if (companies && companies[companyId]) {
        this.setCurrentCompanyContext(companies[companyId]);
      }
    });
  }
}
